import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config, { logger } from '../../config';
import {
  getToken,
  getOnboardingAIData,
  updateV2OnboardingAiResult,
  handleApiError,
} from '../../utils/api';
import {
  calculateAllFinancialDataAI,
  normalizeOnboardingProgressKeys,
  calculatedFinancialDataToV2OnboardingAiResult,
  onboardingProgressFromV2OnboardingAiResult,
  getCareerShiftRunwayData,
} from '../../utils/onboardingUtils';
import {
  getMissingRequiredDashboardFields,
  isMarried,
  saveOnboardingAIChatToStorage,
} from '../../utils/helper';
import {
  incomeSourcesEqual,
  incomeSourcesForSave,
  resolveIncomeSources,
} from './IncomeSourcesSection';

function validProgress(progress) {
  const normalized = normalizeOnboardingProgressKeys(progress);
  if (!normalized || typeof normalized !== 'object' || Array.isArray(normalized)) return null;
  if (getMissingRequiredDashboardFields(normalized).length > 0) return null;
  return normalized;
}

function progressFromUserData(userData) {
  const cached = userData?.v2_onboarding_ai_result;
  if (!cached || typeof cached !== 'object' || Array.isArray(cached)) return null;
  const progress = onboardingProgressFromV2OnboardingAiResult(cached) || cached.onboardingProgress || null;
  return validProgress(progress);
}

export default function useExplorerWhatIf() {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const loadGenerationRef = useRef(0);

  const [isLoading, setIsLoading] = useState(true);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [isRunningScenario, setIsRunningScenario] = useState(false);
  const [isOpeningForecast, setIsOpeningForecast] = useState(false);
  const [scenarioStatus, setScenarioStatus] = useState('idle');
  const [scenarioMessage, setScenarioMessage] = useState('');
  const [incomeSources, setIncomeSources] = useState(() => resolveIncomeSources(null));
  const [savedIncomeSources, setSavedIncomeSources] = useState(() => resolveIncomeSources(null));
  const incomeSourcesRef = useRef(incomeSources);
  incomeSourcesRef.current = incomeSources;

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    const loadGeneration = ++loadGenerationRef.current;

    const loadProgress = async () => {
      try {
        const token = getToken();
        if (!token) {
          setIsLoading(false);
          return;
        }

        const apiData = await getOnboardingAIData(token, navigate, false, signal);
        if (loadGeneration !== loadGenerationRef.current || signal?.aborted) return;

        if (!apiData?.success) {
          setIsLoading(false);
          return;
        }

        saveOnboardingAIChatToStorage(apiData, config);

        const loadedIncomeSources = resolveIncomeSources(apiData.data?.income_sources);
        incomeSourcesRef.current = loadedIncomeSources;
        setIncomeSources(loadedIncomeSources);
        setSavedIncomeSources(loadedIncomeSources);

        const cachedProgress = progressFromUserData(apiData.data);
        if (cachedProgress) {
          setOnboardingProgress(cachedProgress);
          setIsLoading(false);
          return;
        }

        const chatData = apiData.data?.v2_onboarding_ai?.chat || [];
        if (chatData.length === 0) {
          setIsLoading(false);
          return;
        }

        let financialDataResult;
        try {
          financialDataResult = await calculateAllFinancialDataAI(chatData);
        } catch (error) {
          logger.error('Error calculating explorer what-if data:', error);
          setIsLoading(false);
          return;
        }

        if (loadGeneration !== loadGenerationRef.current || signal?.aborted) return;

        const convertedProgress = validProgress(financialDataResult?.onboardingProgress || {});
        if (!convertedProgress) {
          setIsLoading(false);
          return;
        }

        const result = await updateV2OnboardingAiResult(
          {
            token,
            v2OnboardingAiResult: calculatedFinancialDataToV2OnboardingAiResult(financialDataResult),
          },
          navigate
        );
        if (!result.ok) {
          logger.warn('Failed to save calculated data:', result.message || 'Unknown error');
        }

        if (loadGeneration !== loadGenerationRef.current || signal?.aborted) return;
        setOnboardingProgress(convertedProgress);
        setIsLoading(false);
      } catch (error) {
        if (loadGeneration !== loadGenerationRef.current || signal?.aborted) return;
        logger.error('Error loading explorer what-if data:', error);
        setIsLoading(false);
        handleApiError(error, navigate);
      }
    };

    loadProgress();
    return () => {
      loadGenerationRef.current += 1;
      abortControllerRef.current?.abort();
    };
  }, [navigate]);

  useEffect(() => {
    if (!isOpeningForecast) return undefined;
    const id = window.setTimeout(() => {
      navigate('/dashboard/forecast', { state: { scenarioApplied: true } });
    }, 450);
    return () => window.clearTimeout(id);
  }, [isOpeningForecast, navigate]);

  const runway = useMemo(
    () => getCareerShiftRunwayData(onboardingProgress),
    [onboardingProgress]
  );

  const partnerMonthlyIncome = useMemo(() => {
    const value = Number(onboardingProgress?.monthly_net_income_partner);
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }, [onboardingProgress]);

  const userMonthlyIncome = useMemo(() => {
    const combined = Number(runway.currentMonthlyIncome) || 0;
    const partnerShare = isMarried(onboardingProgress?.family_situation) ? partnerMonthlyIncome : 0;
    return Math.max(0, combined - partnerShare);
  }, [onboardingProgress, partnerMonthlyIncome, runway.currentMonthlyIncome]);

  const currentRetirementAge = useMemo(() => {
    const age = parseInt(onboardingProgress?.retirement_age, 10);
    return Number.isFinite(age) && age > 0 ? age : 65;
  }, [onboardingProgress]);

  const hasIncomeSourceChanges = useMemo(
    () => !incomeSourcesEqual(incomeSources, savedIncomeSources),
    [incomeSources, savedIncomeSources]
  );

  const handleIncomeSourcesChange = useCallback((updater) => {
    setIncomeSources((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      incomeSourcesRef.current = next;
      return next;
    });
  }, []);

  const handleRunScenario = useCallback(async (updates) => {
    if (!onboardingProgress || typeof onboardingProgress !== 'object' || Array.isArray(onboardingProgress)) {
      setScenarioStatus('error');
      setScenarioMessage('Your financial profile is still loading. Please try again.');
      return;
    }

    const token = getToken();
    if (!token) {
      setScenarioStatus('error');
      setScenarioMessage('Please sign in again to run this scenario.');
      return;
    }

    const nextDuringShift = Math.max(0, Number(updates?.monthly_net_income_during_career_shift) || 0);
    const nextPartner = Math.max(0, Number(updates?.monthly_net_income_partner) || 0);
    const nextExpenses = Math.max(0, Number(updates?.monthly_expenses) || 0);
    const nextRetAge = Math.max(0, Number(updates?.retirement_age) || 0);
    const nextIncomeSources = incomeSourcesForSave(incomeSourcesRef.current);
    const displayedIncomeSources = resolveIncomeSources(nextIncomeSources);

    const nextProfile = {
      ...onboardingProgress,
      monthly_net_income_during_career_shift: String(nextDuringShift),
      monthly_net_income_partner: String(nextPartner),
      monthly_expenses: String(nextExpenses),
      retirement_age: String(nextRetAge),
    };

    setIsRunningScenario(true);
    setScenarioStatus('running');
    setScenarioMessage('Saving your scenario…');
    try {
      const result = await updateV2OnboardingAiResult(
        {
          token,
          v2OnboardingAiResult: {
            financial_profile: nextProfile,
            updated_at: Date.now(),
          },
          incomeSources: nextIncomeSources,
        },
        navigate
      );

      if (!result?.ok) {
        logger.warn('Failed to save what-if scenario:', result?.message || 'Unknown error');
        setIsRunningScenario(false);
        setScenarioStatus('error');
        setScenarioMessage('Unable to update your scenario. Please try again.');
        return;
      }

      setOnboardingProgress(nextProfile);
      setIncomeSources(displayedIncomeSources);
      setSavedIncomeSources(displayedIncomeSources);
      incomeSourcesRef.current = displayedIncomeSources;
      setScenarioMessage('Opening your forecast…');
      setIsOpeningForecast(true);
    } catch (error) {
      logger.error('Error saving what-if scenario:', error);
      setIsRunningScenario(false);
      setScenarioStatus('error');
      setScenarioMessage('Unable to update your scenario. Please try again.');
    }
  }, [navigate, onboardingProgress]);

  return {
    isLoading,
    userMonthlyIncome,
    partnerMonthlyIncome,
    currentMonthlyExpenses: Number(runway.currentMonthlyExpenses) || 0,
    currentRetirementAge,
    isRunningScenario,
    isOpeningForecast,
    onRunScenario: handleRunScenario,
    scenarioStatus,
    scenarioMessage,
    incomeSources,
    onIncomeSourcesChange: handleIncomeSourcesChange,
    hasIncomeSourceChanges,
  };
}
